/**
 * pdfBuilder.ts
 *
 * Reusable low-level jsPDF helpers for building custom-layout PDFs.
 * No report-specific logic here — only primitives that any feature can compose.
 *
 * Design goals:
 *  - Stateless helpers that receive a `doc` and current Y, return the new Y.
 *  - All positions in mm (jsPDF default).
 *  - Callers own page-break logic via `ensureSpace`.
 */

import jsPDF from 'jspdf';

// ─── Page geometry ────────────────────────────────────────────────────────────

export const PAGE_W = 210;
export const PAGE_H = 297;
export const MARGIN_L = 14;
export const MARGIN_R = 14;
export const MARGIN_T = 16;
export const MARGIN_B = 16;
export const CONTENT_W = PAGE_W - MARGIN_L - MARGIN_R; // 182 mm

// Right-edge X positions for right-aligned value columns
export const COL_PCT_X = PAGE_W - MARGIN_R; // percentage — far right
export const COL_DUR_X = COL_PCT_X - 22; // duration — left of percentage

// ─── Colour palette ───────────────────────────────────────────────────────────

export const COLOR = {
  title: [30, 40, 70] as [number, number, number],
  body: [50, 60, 80] as [number, number, number],
  muted: [150, 155, 165] as [number, number, number],
  brand: [30, 40, 70] as [number, number, number],
  line: [210, 215, 225] as [number, number, number],
  lineBold: [170, 175, 195] as [number, number, number],
};

// ─── Typography scale ─────────────────────────────────────────────────────────

export interface TextStyle {
  size: number;
  style: 'normal' | 'bold' | 'italic';
  color: [number, number, number];
}

export const TEXT: Record<string, TextStyle> = {
  brand: { size: 16, style: 'bold', color: COLOR.brand },
  title: { size: 13, style: 'bold', color: COLOR.title },
  subtitle: { size: 8, style: 'normal', color: COLOR.muted },
  stat: { size: 9, style: 'bold', color: COLOR.body },
  statLbl: { size: 7, style: 'normal', color: COLOR.muted },
  sectionTitle: { size: 8, style: 'bold', color: COLOR.muted },
  l1: { size: 10, style: 'bold', color: COLOR.title },
  l2: { size: 8.5, style: 'normal', color: COLOR.body },
  l3: { size: 7.5, style: 'normal', color: COLOR.body },
  l4: { size: 7, style: 'normal', color: COLOR.muted },
  l5: { size: 7, style: 'normal', color: COLOR.muted },
};

// ─── Row geometry ─────────────────────────────────────────────────────────────

// Vertical padding above text baseline within each row
const ROW_PAD_TOP: Record<number, number> = { 0: 4, 1: 3, 2: 2.5 };
// Height of the text line (baseline → next row start, before separator)
const ROW_H: Record<number, number> = { 0: 6.5, 1: 5.5, 2: 5 };
// Gap between text baseline and the separator line drawn below each row
const SEP_GAP: Record<number, number> = { 0: 4, 1: 3.5, 2: 3 };

const getRowPadTop = (d: number) => ROW_PAD_TOP[Math.min(d, 2)] ?? 2.5;
const getRowH = (d: number) => ROW_H[Math.min(d, 2)] ?? 5;
const getSepGap = (d: number) => SEP_GAP[Math.min(d, 2)] ?? 2;

/** Total vertical space consumed by a tree row (for ensureSpace calls). */
export function treeRowHeight(depth: number): number {
  return getRowPadTop(depth) + getRowH(depth) + getSepGap(depth) + 0.2;
}

// ─── Core primitives ──────────────────────────────────────────────────────────

export function applyStyle(doc: jsPDF, style: TextStyle): void {
  doc.setFontSize(style.size);
  doc.setFont('helvetica', style.style);
  doc.setTextColor(...style.color);
}

export function drawText(
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  style: TextStyle,
  maxWidth?: number,
): number {
  applyStyle(doc, style);
  const opts: Record<string, unknown> = {};
  if (maxWidth) opts.maxWidth = maxWidth;
  doc.text(text, x, y, opts);
  return doc.getTextWidth(text);
}

export function drawTextRight(
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  style: TextStyle,
): void {
  applyStyle(doc, style);
  doc.text(text, x, y, { align: 'right' });
}

export function drawRule(
  doc: jsPDF,
  y: number,
  color: [number, number, number] = COLOR.line,
  thickness = 0.2,
): void {
  doc.setDrawColor(...color);
  doc.setLineWidth(thickness);
  doc.line(MARGIN_L, y, PAGE_W - MARGIN_R, y);
}

export function drawRuleFrom(
  doc: jsPDF,
  y: number,
  startX: number,
  color: [number, number, number] = COLOR.line,
  thickness = 0.15,
): void {
  doc.setDrawColor(...color);
  doc.setLineWidth(thickness);
  doc.line(startX, y, PAGE_W - MARGIN_R, y);
}

export function drawImage(
  doc: jsPDF,
  base64: string,
  format: 'PNG' | 'JPEG',
  x: number,
  y: number,
  w: number,
  h: number,
): number {
  doc.addImage(base64, format, x, y, w, h);
  return y + h;
}

export function ensureSpace(doc: jsPDF, y: number, needed: number): number {
  if (y + needed > PAGE_H - MARGIN_B) {
    doc.addPage();
    return MARGIN_T;
  }
  return y;
}

// ─── SVG → PNG conversion (browser only) ─────────────────────────────────────

export async function svgToPngBase64(svgText: string, size = 64): Promise<string> {
  return new Promise((resolve) => {
    const blob = new Blob([svgText], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.drawImage(img, 0, 0, size, size);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve('');
    };
    img.src = url;
  });
}

// ─── Header block ─────────────────────────────────────────────────────────────

const LOGO_SIZE_MM = 7; // logo square size in mm

export function drawHeader(
  doc: jsPDF,
  title: string,
  dateRange: string,
  y: number = MARGIN_T,
  logoBase64?: string,
): number {
  // Brand block: [logo] [ZeLog text] — logo first, text immediately to its right
  applyStyle(doc, TEXT.brand);
  const brandTextW = doc.getTextWidth('ZeLog');
  // Total block width = logo + gap + text, right-edge at PAGE_W - MARGIN_R
  const blockRight = PAGE_W - MARGIN_R;
  const textX = blockRight - brandTextW; // text left edge
  const logoX = textX - 2 - LOGO_SIZE_MM; // logo sits left of text
  const logoY = y;
  const textBaselineY = logoY + LOGO_SIZE_MM * 0.82; // baseline ≈ 82% down the logo height
  if (logoBase64) {
    doc.addImage(logoBase64, 'PNG', logoX, logoY, LOGO_SIZE_MM, LOGO_SIZE_MM);
  }
  drawText(doc, 'ZeLog', textX, textBaselineY, TEXT.brand);
  drawText(doc, title, MARGIN_L, y + 2, TEXT.title);
  y += 9;
  drawText(doc, dateRange, MARGIN_L, y, TEXT.subtitle);
  y += 7;
  return y;
}

// ─── Stats row ────────────────────────────────────────────────────────────────

export interface StatItem {
  label: string;
  value: string;
}

export function drawStats(doc: jsPDF, stats: StatItem[], y: number): number {
  const colW = CONTENT_W / stats.length;
  stats.forEach((s, i) => {
    const x = MARGIN_L + i * colW;
    drawText(doc, s.label, x, y, TEXT.statLbl);
    const lblW = doc.getTextWidth(s.label) + 2;
    drawText(doc, s.value, x + lblW, y, TEXT.stat);
  });
  y += 5;
  y += 5;
  return y;
}

// ─── Native donut chart ───────────────────────────────────────────────────────

export interface DonutSliceDef {
  label: string;
  value: number; // seconds or any numeric magnitude
  color: string; // hex e.g. "#e53935"
}

/**
 * Draw a native jsPDF donut chart (no HTML capture).
 * cx/cy = centre in mm, outerR/innerR = radii in mm.
 * Returns the new Y (bottom edge of the chart + label area).
 */
export function drawDonut(
  doc: jsPDF,
  slices: DonutSliceDef[],
  cx: number,
  cy: number,
  outerR: number,
  innerR: number,
  centerLabel?: string,
): void {
  const total = slices.reduce((s, x) => s + x.value, 0);
  if (!total) return;

  let angle = -Math.PI / 2; // start at top

  for (const slice of slices) {
    const sweep = (slice.value / total) * 2 * Math.PI;
    const endAngle = angle + sweep;

    // Parse hex color
    const hex = slice.color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    doc.setFillColor(r, g, b);
    doc.setDrawColor(r, g, b);
    doc.setLineWidth(0);

    // Draw filled sector by approximating with many small triangles
    const steps = Math.max(2, Math.ceil((sweep / (2 * Math.PI)) * 60));
    const pts: Array<[number, number]> = [];
    for (let i = 0; i <= steps; i++) {
      const a = angle + (sweep * i) / steps;
      pts.push([cx + outerR * Math.cos(a), cy + outerR * Math.sin(a)]);
    }
    // Build the sector path: outer arc forward, then back to centre
    // Use lines — jsPDF doesn't have native arc-fill, so approximate
    const path: Array<[number, number]> = [
      [cx + outerR * Math.cos(angle), cy + outerR * Math.sin(angle)],
      ...pts,
      [cx, cy],
    ];
    doc.triangle(path[0][0], path[0][1], path[1][0], path[1][1], cx, cy, 'F');
    for (let i = 1; i < pts.length; i++) {
      doc.triangle(cx, cy, pts[i - 1][0], pts[i - 1][1], pts[i][0], pts[i][1], 'F');
    }

    angle = endAngle;
  }

  // Punch out inner circle (donut hole) in white
  doc.setFillColor(255, 255, 255);
  doc.circle(cx, cy, innerR, 'F');

  // Centre label
  if (centerLabel) {
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(60, 60, 60);
    const tw = doc.getTextWidth(centerLabel);
    doc.text(centerLabel, cx - tw / 2, cy + 2);
  }
}

/**
 * Draw a donut with a legend list to its right.
 * Each legend row shows: colour dot · label · duration · percentage
 * title = section heading above the donut+legend pair.
 * Returns Y after the section.
 */
export function drawDonutWithLegend(
  doc: jsPDF,
  title: string,
  slices: DonutSliceDef[],
  totalLabel: string,
  formatDurationFn: (secs: number) => string,
  y: number,
): number {
  const DONUT_R = 22;
  const HOLE_R = 13;
  const DONUT_D = DONUT_R * 2;

  // Keep the donut circle intact — never split it across pages
  y = ensureSpace(doc, y, DONUT_D + 6 + 8); // title(6) + donut diameter + breathing room

  // Section title
  drawText(doc, title, MARGIN_L, y, TEXT.sectionTitle);
  drawRule(doc, y + 2, COLOR.line, 0.2);
  y += 6;

  const cx = MARGIN_L + DONUT_R + 2;
  const cy = y + DONUT_R;

  drawDonut(doc, slices, cx, cy, DONUT_R, HOLE_R, totalLabel);

  // Legend — to the right of the donut
  // Right column layout: [pct] at far right, [duration] left of pct
  const COL_LGND_PCT = COL_PCT_X; // percentage — far right
  const COL_LGND_DUR = COL_LGND_PCT - 14; // duration — left of pct
  const legendX = cx + DONUT_R + 6;
  const legendTextX = legendX + 4; // text starts after dot (1.2 radius + gap)
  const legendMaxW = COL_LGND_DUR - legendTextX - 14; // 14mm gap before duration column
  const total = slices.reduce((s, x) => s + x.value, 0);

  let legY = y + 3;
  const lineH = 5.2;
  // Track whether we've spilled onto a continuation page
  let onContinuationPage = false;

  for (const slice of slices) {
    const rowLegendX = legendX;
    const lines: string[] = doc.splitTextToSize(slice.label, legendMaxW);
    const rowHeight = lines.length * lineH;

    // Page-break: if this row won't fit, add a new page and resume from top
    if (legY + rowHeight > PAGE_H - MARGIN_B) {
      doc.addPage();
      legY = MARGIN_T;
      onContinuationPage = true;
    }

    const hex = slice.color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    doc.setFillColor(r, g, b);
    doc.circle(rowLegendX + 1.2, legY - 0.8, 0.6, 'F');

    // Render each wrapped line of the label
    applyStyle(doc, TEXT.l3);
    for (let i = 0; i < lines.length; i++) {
      doc.text(lines[i], legendTextX, legY + i * lineH);
    }

    // Duration and percentage anchored to first line
    const dur = formatDurationFn(slice.value);
    drawTextRight(doc, dur, COL_LGND_DUR, legY, TEXT.l3);

    const pct = total > 0 ? `${((slice.value / total) * 100).toFixed(1)}%` : '0%';
    drawTextRight(doc, pct, COL_LGND_PCT, legY, { ...TEXT.l3, color: COLOR.muted });

    // Divider below the last line of this row
    drawRuleFrom(doc, legY + rowHeight - lineH + 1.8, rowLegendX, COLOR.line, 0.1);

    legY += rowHeight + 2;
  }

  if (onContinuationPage) {
    return legY + 8;
  }
  return Math.max(legY, y + DONUT_D) + 8;
}

// ─── Native project distribution bar chart ───────────────────────────────────

export interface BarChartSlice {
  label: string;
  value: number; // seconds
  color: string; // hex
}

/** Project entry used for the legend beneath the stacked bar chart. */
export interface BarChartProject {
  id: string;
  label: string;
  color: string;
}

/**
 * Draw a Clockify-style vertical stacked column chart.
 * X-axis = date buckets (e.g. "Jan 01"), Y-axis = hours.
 * Each column is stacked by project, coloured per project.
 * buckets: ordered date-key strings (YYYY-MM-DD)
 * data:    Map from bucket key → array of slices (one per project)
 * projects: ordered project legend entries
 * Returns Y after the chart (including legend).
 */
export function drawProjectBarChart(
  doc: jsPDF,
  title: string,
  buckets: string[],
  data: Map<string, BarChartSlice[]>,
  formatDurationFn: (secs: number) => string,
  y: number,
  projects?: BarChartProject[],
): number {
  if (!buckets.length) return y;

  const CHART_H = 40; // total height of the chart area in mm
  const AXIS_H = 6; // space below bars for X-axis labels
  const AXIS_Y_W = 8; // left margin for Y-axis labels
  const chartLeft = MARGIN_L + AXIS_Y_W;
  const chartW = CONTENT_W - AXIS_Y_W;
  const totalH = CHART_H + AXIS_H + 14;

  y = ensureSpace(doc, y, totalH + 8);

  drawText(doc, title, MARGIN_L, y, TEXT.sectionTitle);
  y += 6;

  const chartTop = y;

  // Find max total (in seconds) across all buckets
  let maxSecs = 0;
  data.forEach((slices) => {
    const total = slices.reduce((s: number, x: BarChartSlice) => s + x.value, 0);
    if (total > maxSecs) maxSecs = total;
  });
  if (!maxSecs) return y + CHART_H + AXIS_H + 4;

  const maxHours = maxSecs / 3600;

  // Y-axis grid lines & labels (4 evenly-spaced ticks)
  const yTicks = 4;
  for (let t = 0; t <= yTicks; t++) {
    const hrs = (maxHours * t) / yTicks;
    const lineY = chartTop + CHART_H - (CHART_H * t) / yTicks;
    // Grid line
    doc.setDrawColor(...COLOR.line);
    doc.setLineWidth(0.1);
    doc.line(chartLeft, lineY, chartLeft + chartW, lineY);
    // Label
    doc.setFontSize(5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLOR.muted);
    const lbl = hrs < 1 ? `${Math.round(hrs * 60)}m` : `${hrs.toFixed(hrs < 10 ? 1 : 0)}h`;
    const lw = doc.getTextWidth(lbl);
    doc.text(lbl, chartLeft - lw - 1, lineY + 1.2);
  }

  // Columns
  const n = buckets.length;
  const colW = chartW / n;
  const barW = Math.max(1.5, colW * 0.65);
  const barOffX = (colW - barW) / 2;

  buckets.forEach((bucket, i) => {
    const slices = data.get(bucket) ?? [];
    const colLeft = chartLeft + i * colW;

    // Stack bars bottom-up
    let stackY = chartTop + CHART_H;
    for (const slice of slices) {
      if (!slice.value) continue;
      const barH = (slice.value / maxSecs) * CHART_H;
      const hex = slice.color.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      doc.setFillColor(r, g, b);
      doc.rect(colLeft + barOffX, stackY - barH, barW, barH, 'F');
      stackY -= barH;
    }

    // X-axis label (Mon 01 style from YYYY-MM-DD)
    const shortLabel =
      bucket.length >= 10
        ? (() => {
            const months = [
              'Jan',
              'Feb',
              'Mar',
              'Apr',
              'May',
              'Jun',
              'Jul',
              'Aug',
              'Sep',
              'Oct',
              'Nov',
              'Dec',
            ];
            const parts = bucket.split('-');
            const mon = months[parseInt(parts[1], 10) - 1] ?? '';
            return `${mon} ${parts[2]}`;
          })()
        : bucket;
    doc.setFontSize(5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLOR.muted);
    const lw = doc.getTextWidth(shortLabel);
    doc.text(shortLabel, colLeft + colW / 2 - lw / 2, chartTop + CHART_H + AXIS_H - 1);
  });

  y = chartTop + CHART_H + AXIS_H + 2;

  // Legend row (project colour dots + names)
  // if (projects && projects.length > 0) {
  //   const DOT_R = 1.2;
  //   const ITEM_GAP = 3;
  //   const ITEM_H = 4;
  //   let legX = chartLeft;
  //   let legY = y + 3;
  //   for (const proj of projects) {
  //     const hex = proj.color.replace('#', '');
  //     const r = parseInt(hex.substring(0, 2), 16);
  //     const g = parseInt(hex.substring(2, 4), 16);
  //     const b = parseInt(hex.substring(4, 6), 16);
  //     doc.setFillColor(r, g, b);
  //     doc.circle(legX + DOT_R, legY - DOT_R + 0.5, DOT_R, 'F');
  //     doc.setFontSize(5.5);
  //     doc.setFont('helvetica', 'normal');
  //     doc.setTextColor(...COLOR.body);
  //     const truncated = doc.splitTextToSize(proj.label, 28)[0];
  //     doc.text(truncated, legX + DOT_R * 2 + 1.5, legY);
  //     const itemW = DOT_R * 2 + 1.5 + doc.getTextWidth(truncated) + ITEM_GAP;
  //     legX += itemW;
  //     // Wrap to next line if needed
  //     if (legX > MARGIN_L + CONTENT_W - 20) {
  //       legX = chartLeft;
  //       legY += ITEM_H;
  //     }
  //   }
  //   y = legY + 4;
  // } else {
  //   y += 4;
  // }
  y += 4;
  return y;
}

// ─── Tree row ─────────────────────────────────────────────────────────────────

export interface TreeRowConfig {
  depth: number;
  label: string;
  duration: string;
  dotColor?: string;
}

const depthStyle = (depth: number): TextStyle =>
  depth === 0 ? TEXT.l1 : depth === 1 ? TEXT.l2 : TEXT.l5;

/**
 * Draw a single tree row — duration only, no percentage column (matches Clockify tree layout).
 * A separator line is drawn BELOW every row indented to match depth.
 * Returns Y after the row (including separator + trailing gap).
 */
export function drawTreeRow(doc: jsPDF, row: TreeRowConfig, y: number): number {
  const indentX = MARGIN_L + row.depth * 7;
  const style = depthStyle(row.depth);

  // Padding above text
  y += getRowPadTop(row.depth);

  // Colour dot (typically depth 0 for project grouping)
  let labelX = indentX;
  if (row.dotColor) {
    const hex = row.dotColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    doc.setFillColor(r, g, b);
    doc.circle(labelX + 1.5, y - 1, 0.8, 'F');
    labelX += 5;
  }

  // Label — wrap within available width, leave space for duration at right
  const maxLabelW = COL_PCT_X - labelX - 24;
  applyStyle(doc, style);
  const labelLines: string[] = doc.splitTextToSize(row.label, maxLabelW);
  const rowLineH = getRowH(row.depth);
  for (let i = 0; i < labelLines.length; i++) {
    doc.text(labelLines[i], labelX, y + i * rowLineH);
  }

  // Duration — right-aligned at far right, anchored to first line
  drawTextRight(doc, row.duration, COL_PCT_X, y, style);

  // Advance past all wrapped lines
  y += (labelLines.length - 1) * rowLineH;

  // Separator line below every row, indented to depth
  y += getSepGap(row.depth);
  const lineColor = row.depth === 0 ? COLOR.lineBold : COLOR.line;
  const lineThickness = row.depth === 0 ? 0.25 : 0.15;
  drawRuleFrom(doc, y, indentX, lineColor, lineThickness);

  y += 2;

  return y + 0.2;
}
